import React, { useEffect, useRef } from 'react';
import OdooSyncBadge from './OdooSyncBadge';
import { formatDateTime } from '../../../lib/formatDate';
import { leaverChip } from './leavers';

/**
 * Users table — styled to match the other .admin-table tabs (categories,
 * activity logs, tool requests).
 *
 * Columns: User · Status · Departments · Last sign-in.
 * Bulk-selection is parked for now (no per-row checkbox, no bulk bar).
 *
 * Keyboard nav still works (ArrowUp/Down/Home/End to move selection,
 * Enter to open the drawer).
 *
 * Props (unchanged shape — onBulk* + selectedBulk are accepted but unused
 * to avoid wiring churn in AdminUsersTab):
 *   users               filtered + sorted list to display
 *   selectedId          number | null
 *   onSelect            (user) => void
 *   sort                'name' | 'last_seen' | 'recent'
 *   onSortChange        (sort) => void
 *   search              string
 *   onSearchChange      (s) => void
 *   notice              string | null   inline notice above the table (Leavers states)
 *   emptyText           string          copy for an empty list (defaults to the filters line)
 *   hideEmpty           boolean         no empty row (the notice already explains why)
 *   renderRowAction     (user) => node  optional per-row action under the Status cell
 */
export default function UserList({
  users,
  selectedId,
  onSelect,
  sort,
  onSortChange,
  search,
  onSearchChange,
  notice = null,
  emptyText = 'No users match the current filters.',
  hideEmpty = false,
  renderRowAction = null,
}) {
  const rowRefs = useRef(new Map());

  const onKeyDown = (e) => {
    if (users.length === 0) return;
    const currentIdx = selectedId
      ? users.findIndex((u) => u.id === selectedId)
      : -1;
    let next = currentIdx;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      next = currentIdx < 0 ? 0 : Math.min(users.length - 1, currentIdx + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      next = currentIdx < 0 ? 0 : Math.max(0, currentIdx - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = users.length - 1;
    } else if (e.key === 'Enter' && currentIdx >= 0) {
      e.preventDefault();
      onSelect(users[currentIdx]);
      return;
    } else {
      return;
    }
    if (next !== currentIdx && next >= 0) {
      onSelect(users[next]);
      const row = rowRefs.current.get(users[next].id);
      row?.focus();
      row?.scrollIntoView({ block: 'nearest' });
    }
  };

  useEffect(() => {
    if (!selectedId) return;
    const row = rowRefs.current.get(selectedId);
    if (row && document.activeElement === document.body) {
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedId]);

  return (
    <div className="ut-listpane">
      <div className="ut-listbar">
        <input
          type="search"
          placeholder="Search users by name or email…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="Search users"
        />
        <select
          className="ut-sort"
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          aria-label="Sort users"
        >
          <option value="name">Sort: Name</option>
          <option value="last_seen">Sort: Last sign-in</option>
          <option value="recent">Sort: Recently added</option>
        </select>
      </div>

      {notice && <p className="ut-notice" role="status">{notice}</p>}

      <div className="admin-table-wrap ut-tablewrap">
        <table
          className="admin-table ut-table"
          role="listbox"
          aria-label="Users"
          aria-activedescendant={selectedId ? `ut-user-${selectedId}` : undefined}
          onKeyDown={onKeyDown}
          tabIndex={0}
        >
          <thead>
            <tr>
              <th>User</th>
              <th style={{ width: '180px' }}>Status</th>
              <th>Departments</th>
              <th style={{ width: '160px' }}>Last sign-in</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !hideEmpty && (
              <tr><td colSpan="4" className="at-empty">{emptyText}</td></tr>
            )}
            {users.map((u) => {
              const isSelected = u.id === selectedId;
              const isInactive = u.is_active === false;
              const initials = (u.name || '?')
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((s) => s[0]?.toUpperCase() || '')
                .join('') || '?';
              const deptNames = (u.departments || []).map((d) => d.name).filter(Boolean);
              const chip = leaverChip(u);
              return (
                <tr
                  key={u.id}
                  id={`ut-user-${u.id}`}
                  ref={(el) => { if (el) rowRefs.current.set(u.id, el); else rowRefs.current.delete(u.id); }}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  className={`ut-tr ${isSelected ? 'is-selected' : ''} ${isInactive ? 'is-inactive' : ''}`}
                  onClick={() => onSelect(u)}
                >
                  <td className="at-primary">
                    <div className="ut-cell-user">
                      {u.photo_url ? (
                        <img
                          src={u.photo_url}
                          alt=""
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          className="ut-cell-avatar"
                        />
                      ) : (
                        <span className="ut-cell-avatar ut-cell-avatar--fallback" aria-hidden="true">
                          {initials}
                        </span>
                      )}
                      <div className="ut-cell-user-text">
                        <div className="ut-cell-name">{u.name}</div>
                        <div className="ut-cell-email">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="ut-cell-pills">
                      {u.is_admin && <span className="at-pill ut-pill--admin">Admin</span>}
                      {!u.is_admin && (u.roles || []).includes('dept_manager') && (
                        <span className="at-pill ut-pill--manager">Manager</span>
                      )}
                      {!u.is_admin && !(u.roles || []).includes('dept_manager') && !isInactive && (
                        <span className="at-pill ut-pill--employee">Employee</span>
                      )}
                      {isInactive && <span className="at-pill ut-pill--inactive">Inactive</span>}
                      {chip && <span className={`at-pill at-pill--${chip.tone}`}>{chip.label}</span>}
                    </div>
                    {chip && chip.meta && <small className="ut-leaver-meta">{chip.meta}</small>}
                    {renderRowAction && <div className="ut-row-action">{renderRowAction(u)}</div>}
                  </td>
                  <td className="ut-cell-depts">
                    <div className="ut-cell-depts-inner">
                      <span className="ut-cell-depts-names">
                        {deptNames.length === 0
                          ? <span className="ut-muted">—</span>
                          : deptNames.join(', ')}
                      </span>
                      <OdooSyncBadge status={u.odoo_sync_status} />
                    </div>
                  </td>
                  <td className="ut-cell-time">
                    {u.last_signed_in
                      ? (
                          <div className="ut-time-stack">
                            <span>{formatDateTime(u.last_signed_in)}</span>
                            <small>{formatRelative(u.last_signed_in)}</small>
                          </div>
                        )
                      : <span className="ut-muted">Never</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function formatRelative(iso) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}
